import {
    useLoggedOut,
    useLoggedOutJira,
    useLoggedIn,
    useHomePageJSMXflowTargetingCohort,
    useHomePageJPDXflowTargetingCohort,
    useJiraW2AndAfterOrMultipleJiras,
    useLoggedOutHasExistingSites,
    useLoggedOutHasNoExistingSites,
    useNewUser,
    useSpecifiedPathnames,
    useHomePageLoomXflowTargetingCohort,
    usePrefersDarkColorScheme,
    useIsDesktop,
    useMultipleTransactionAccountsCohort
} from "@/components/user-context";
import { useLocation } from "@/utils/location-provider";

import { useEnglishLocale } from "../sentry-intl";

import { Manifest } from ".";

const booleanTypeGuard = (value: unknown): value is boolean =>
    typeof value === "boolean";

export const experiments = {
    gwp8911NotificationBell: {
        experimentName: "wac_notification_bell_experiment",
        parameterName: "cohort",
        defaultValue: "control",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return useLoggedIn() && useIsDesktop();
            }
        },
        metadata: {
            description:
                "notification bell in global nav for logged-in desktop users",
            expectedReleaseDate: "01/16/2026",
            expectedRuntime: "10 weeks",
            context: {
                urls: [
                    "/",
                    "/software/jira",
                    "/software/jira/service-management",
                    "/software/confluence",
                    "/software/jira/pricing",
                    "/software/confluence/templates",
                    "/software/confluence/pricing",
                    "/software/jira/product-discovery"
                ]
            }
        }
    },
    gwp8655WacHomepageTWCHero: {
        experimentName: "wac_homepage_twc_hero_experiment",
        parameterName: "cohort",
        defaultValue: "control",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return useLoggedOutHasNoExistingSites();
            }
        },
        metadata: {
            description: "homepage hero promoting TWC for logged-out users",
            expectedReleaseDate: "11/16/2025",
            expectedRuntime: "5 weeks",
            context: {
                urls: ["/"]
            }
        }
    },
    gwp8111SalesInboundLeadsCta: {
        experimentName: "wac-sales-inbound-leads-experiment",
        parameterName: "cohort",
        defaultValue: "control",
        options: {
            fireExperimentExposure: false,
            typeGuard: (
                value: unknown
            ): value is "control" | "test1" | "test2" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test1", "test2"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "global nav cta displayed for logged-in users who are SMB sales leads",
            expectedReleaseDate: "1/16/2025",
            expectedRuntime: "12 weeks",
            context: {
                urls: ["/", "/try"]
            }
        }
    },
    cfind4782LandingPageSubAnchorV3: {
        experimentName: "cfind-4782-landing-page-sub-anchor-v3",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Test if personalizing the landing page off the sub anchor will increase d1to6",
            expectedReleaseDate: "07/14/2025",
            expectedRuntime: "6 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    jfind1506AfterD7GetOrganicUsersBackToProduct: {
        experimentName: "jfind-1506-after-d7-gubtp-organic",
        parameterName: "cohort",
        defaultValue: "control" as
            | "control"
            | "jfind-1506-after-d7-gubtp-organic",
        options: {
            fireExperimentExposure: true,
            typeGuard: (
                value: unknown
            ): value is "control" | "jfind-1506-after-d7-gubtp-organic" => {
                if (typeof value !== "string") {
                    return false;
                }
                return [
                    "control",
                    "jfind-1506-after-d7-gubtp-organic"
                ].includes(value);
            }
        },
        targeting: {
            cohort: useJiraW2AndAfterOrMultipleJiras
        },
        metadata: {
            description:
                "Display Get Users Back to Product screen for users who have one Jira after D7 or multiple Jiras",
            expectedReleaseDate: "06/05/2025",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    jiraOnboardingAiMadlib: {
        experimentName: "jira-onboarding-ai-madlib",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Enable AI Madib during Jira onboarding experience",
            expectedReleaseDate: "11/17/2025",
            expectedRuntime: "24 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    jfind1504AfterD7GetPaidUsersBackToProduct: {
        experimentName: "jfind-1504-after-d7-get-users-back-to-product",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Paid: Display Get Users Back to Product screen for users who have one Jira after D7 or multiple Jiras",
            expectedReleaseDate: "06/05/2025",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    jiraOnboardingResolveTenantId: {
        experimentName: "jira_onboarding_resolve_tenant_id",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Enable resolving tenant ID before Jira onboarding",
            expectedReleaseDate: "10/16/2025",
            expectedRuntime: "7 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    pricingDirectBuyCheckout: {
        experimentName: "pricing_direct_buy_checkout",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "variation",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "variation" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variation"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "AB test for an option to buy a monthly subscription without a trial from the WAC pricing pages",
            expectedReleaseDate: "01/10/2025",
            expectedRuntime: "4 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    jiraProjectOnboardingExtraFieldsForWizards: {
        experimentName: "jira_onboarding_enable_extra_fields_jsw_projects",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Allow all project creations through the wizard to have extra fields on their issue types",
            expectedReleaseDate: "02/06/2025",
            expectedRuntime: "5 weeks",
            context: {
                urls: []
            }
        }
    },
    jfindSimplifiedListViewWithBasicTasks: {
        experimentName: "jfind-simplified-list-view-with-basic-tasks",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Simplifies the list view columns and adds basic sample tasks to the initial project",
            expectedReleaseDate: "17/06/2025",
            expectedRuntime: "131 days",
            context: {
                urls: []
            }
        }
    },
    jfindBasicSampleTasksForScrumOrSoftwareKanban: {
        experimentName: "jira-create-generic-tasks-and-minimize-quickstart",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Adds basic sample tasks to the initial project for Scrum or Software Kanban projects",
            expectedReleaseDate: "22/09/2025",
            expectedRuntime: "37 days",
            context: {
                urls: []
            }
        }
    },
    jiraProjectOnboardingModernizeOldFlows: {
        experimentName: "modernise_old_onboarding_flows",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Show project onboarding wizard for scrum and kanban templates that do not see the wizard",
            expectedReleaseDate: "1/07/2025",
            expectedRuntime: "9 weeks",
            context: {
                urls: []
            }
        }
    },
    jiraProjectOnboardingWizardDevKanban: {
        experimentName: "jira_software_kanban_wizard_m2",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Show project onboarding wizard for dev kanban while provisioning is taking place",
            expectedReleaseDate: "21/03/2024",
            expectedRuntime: "7 weeks",
            context: {
                urls: []
            }
        }
    },
    signupAddPaymentAb: {
        experimentName: "signup_trialing_add_payment_ab",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "test",
        options: {
            // we need to fire our exposure event manually because we can't determine if the user is eligible from here
            fireExperimentExposure: false,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Experiment to enable users with valid sites to go to payment at the sign-up screen",
            expectedReleaseDate: "02/27/25",
            expectedRuntime: "TBD",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    gwp5090ExperimentBannerConfluence: {
        experimentName: "gwp-5090-experiment-banner-confluence",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "butterbar" | "lifted",
        options: {
            fireExperimentExposure: true,
            typeGuard: (
                value: unknown
            ): value is "control" | "butterbar" | "lifted" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "butterbar", "lifted"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Testing butterbar vs lifted banner for Jira Service Management product tour",
            expectedReleaseDate: "02/18/25",
            expectedRuntime: "2 weeks",
            // TODO: GWP-5270 to fill these urls
            context: {
                urls: []
            }
        }
    },
    gwp7617JiraShortTermAiInfusionExperiment: {
        experimentName: "gwp-7617-jira-short-term-ai-infusion-experiment",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "test",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Short term AI",
            expectedReleaseDate: "08/21/25",
            expectedRuntime: "indefinite",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    jfind1315RemoveExpectationOfFreeConfluence: {
        experimentName: "jfind-1315-remove-expectation-of-free-confluence",
        parameterName: "cohort",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Change the subheader copy on the Confluence free try cloud signup page",
            expectedReleaseDate: "4/28/25",
            expectedRuntime: "76 days",
            context: {
                urls: ["/try/cloud/signup?bundle=confluence&edition=free"]
            }
        }
    },
    jfindMatureExampleProjectExperiment: {
        experimentName: "jfind-mature-example-project",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useNewUser
        },
        metadata: {
            description:
                "Supports creating a new example project for Jira customers",
            expectedReleaseDate: "06/02/2025",
            expectedRuntime: "13 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    gwp4948GartnerJiraProductTourExperiment: {
        experimentName: "gwp-4948-gartner-jira-product-tour-experiment",
        parameterName: "variantName",
        defaultValue: "control" as "control" | "test",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "test" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "test"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "gartner section on jira product tour",
            expectedReleaseDate: "April 2025",
            expectedRuntime: "2 weeks",
            clientOnly: true,
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    gwp6441ConfluenceTemplatesProductNavCta: {
        experimentName: "gwp-6441-confluence-templates-product-nav-cta",
        parameterName: "variant",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return !useLoggedOut();
            }
        },
        metadata: {
            description: "Confluence Templates Product Nav CTA experiment",
            expectedReleaseDate: "undefined",
            expectedRuntime: "77 days",
            clientOnly: true,
            context: {
                urls: ["/software/confluence/templates/*"]
            }
        }
    },
    gwp4871JiraTemplatesProductNavCta: {
        experimentName: "gwp-4871-jira-templates-product-nav-cta",
        parameterName: "variant",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Jira Software Templates Pages Product Nav CTA Experiment",
            expectedReleaseDate: "09/12/2025",
            expectedRuntime: "38 days",
            context: {
                urls: ["/software/jira/templates/*"]
            }
        }
    },
    cfind4311JiraEliminateMicrosoftSignup: {
        experimentName: "cfind-4311-jira-eliminate-microsoft-signup",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useLoggedOut
        },
        metadata: {
            description:
                "Eliminate Microsoft social sign up option for logged out users on Jira product tour",
            expectedReleaseDate: "08/18/2025",
            expectedRuntime: "",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    cfind4327ConfEliminateMicrosoftSignup: {
        experimentName: "cfind-4327-conf-eliminate-microsoft-signup",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useLoggedOut
        },
        metadata: {
            description:
                "Eliminate Microsoft social sign up option for logged out users on Confluence product tour",
            expectedReleaseDate: "08/18/2025",
            expectedRuntime: "",
            context: {
                urls: ["/software/confluence"]
            }
        }
    },
    gwp7037HomepageJsmXflow: {
        experimentName: "gwp-7037-homepage-jsm-xflow",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useHomePageJSMXflowTargetingCohort
        },
        metadata: {
            description:
                "Hero for logged in users with Jira, Confluence, and JSM team types",
            expectedReleaseDate: "07/22/2025",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/"]
            }
        }
    },
    jiraOnboardingAutodetectSystemTheme: {
        experimentName: "jira-onboarding-autodetect-system-theme",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: usePrefersDarkColorScheme
        },
        metadata: {
            description:
                "Autodetect system theme and enroll users into experiment",
            expectedReleaseDate: "08/27/2025",
            expectedRuntime: "18 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    gwp7038HomepageJpdXflow: {
        experimentName: "gwp-7038-homepage-jpd-xflow",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useHomePageJPDXflowTargetingCohort
        },
        metadata: {
            description:
                "Hero for logged in users with Jira, Confluence, and JPD team types",
            expectedReleaseDate: "07/22/2025",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/"]
            }
        }
    },
    gwp7473HomepageLoomXflow: {
        experimentName: "gwp-7473-homepage-loom-xflow",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useHomePageLoomXflowTargetingCohort
        },
        metadata: {
            description: "Hero for logged in users with Loom team types",
            expectedReleaseDate: "08/12/2025",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/"]
            }
        }
    },
    jfindOnboardingWizardInviteStep: {
        experimentName: "jfind-add-invite-step-to-onboarding-wizard",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Add invite step to the Jira onboarding wizard",
            expectedReleaseDate: "09/12/2025",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    jfind1968teamTypeSpaceRecommendation: {
        experimentName: "jfind-1968-team-type-and-space-recommendation",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Space name recommendation based on team types",
            expectedReleaseDate: "10/14/2025",
            expectedRuntime: "1 week",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    },
    gwpAJContextualBanditsJira: {
        experimentName: "gwp-aj-contextual-bandits-jira",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                if (
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useSpecifiedPathnames(["/software/jira"]) &&
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useLoggedOut() &&
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEnglishLocale() &&
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useLocation().search.includes("keyword")
                ) {
                    return true;
                }
                return false;
            }
        },
        metadata: {
            description: "Contextual bandits for Jira",
            expectedReleaseDate: "08/15/2025",
            expectedRuntime: "13 weeks",
            context: {
                urls: ["/software/jira"]
            }
        }
    },
    gwpAJContextualBanditsConfluence: {
        experimentName: "gwp-aj-contextual-bandits-confluence",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                if (
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useSpecifiedPathnames(["/software/confluence"]) &&
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useLoggedOut() &&
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useEnglishLocale() &&
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    useLocation().search.includes("keyword")
                ) {
                    return true;
                }
                return false;
            }
        },
        metadata: {
            description: "Contextual bandits for Confluence",
            expectedReleaseDate: "08/15/2025",
            expectedRuntime: "12 weeks",
            context: {
                urls: ["/software/confluence"]
            }
        }
    },
    jiraPricingPageWaeMigration: {
        experimentName: "gwp-7304-jira-pricing-page-wae-migration-exp",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Migration of Jira pricing page to WAE",
            expectedReleaseDate: "08/18/2025",
            expectedRuntime: "TBD", // todo: @bsparks GWP-7529
            context: {
                urls: ["/software/jira/pricing"]
            }
        }
    },
    wacRoutingTableMvpPerf: {
        experimentName: "wac-routing-table-mvp",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is boolean =>
                typeof value === "boolean"
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Measure performance degredation of wac-routing-table in wac-web",
            expectedReleaseDate: "08/28/2025",
            expectedRuntime: "5 days",
            context: {
                urls: ["/software/jira/enterprise"]
            }
        }
    },
    confluencePricingPageWaeMigration: {
        experimentName: "gwp-7750-confluence-pricing-page-wae-migration-exp",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Migration of Confluence pricing page to WAE",
            expectedReleaseDate: "08/26/2025",
            expectedRuntime: "TBD",
            context: {
                urls: ["/software/confluence/pricing"]
            }
        }
    },
    wacAISearchSummary: {
        experimentName: "wac-ai-search-summary",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return useEnglishLocale() && useIsDesktop() && !useLoggedOut();
            }
        },
        metadata: {
            description: "AI Search Summary Experiment",
            expectedReleaseDate: "10/01/2025",
            expectedRuntime: "8 weeks",
            context: {
                // eslint-disable-next-line local/warn-wildcard-usage-eslint
                urls: ["*"]
            }
        }
    },
    cfind4802JiraAccountCreationRedirect: {
        experimentName: "jira_interstitial_page_for_account_creation",
        parameterName: "isEnabled",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: useLoggedOutJira
        },
        metadata: {
            description: "Jira Account Creation Redirect Experiment",
            expectedReleaseDate: "11/15/2025",
            expectedRuntime: "3 weeks",
            context: {
                urls: ["/try/cloud/signup"]
            }
        }
    },
    cfind4026KeepLandUsersOnWac: {
        experimentName: "cfind-4026-keep-land-users-on-wac",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Redirects land users back to wac instead of home when they click sign in on WAC",
            expectedReleaseDate: "10/10/2025",
            expectedRuntime: "6 weeks",
            context: {
                // eslint-disable-next-line
                urls: ["*"]
            }
        }
    },
    gwp8058GeneralSigninPromptForExistingUsers: {
        experimentName: "gwp-8058-general-signin-prompt-for-existing-users",
        parameterName: "variant",
        defaultValue: false,
        options: {
            fireExperimentExposure: false,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return useLoggedOutHasExistingSites() && useIsDesktop();
            }
        },
        metadata: {
            description:
                "General signin prompt for existing users for users home homepage",
            expectedReleaseDate: "10/20/2025",
            expectedRuntime: "8 weeks",
            context: {
                urls: ["/"]
            }
        }
    },
    gwp8627SignupTransactionSelector: {
        experimentName: "gwp-8627-signup-transaction-selector",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                return useLoggedIn() && useMultipleTransactionAccountsCohort();
            }
        },
        metadata: {
            description:
                "Gate for controling if txa selector should be rendered on Site Activation",
            expectedReleaseDate: "09/30/2025",
            expectedRuntime: "",
            context: {
                urls: ["/try/cloud/signup"]
            }
        }
    },
    gwp8288PersonalizingWacSoftwarePage: {
        experimentName: "gwp-8288-personalizing-wac-software-page",
        parameterName: "enrolled",
        defaultValue: false,
        options: {
            fireExperimentExposure: true,
            typeGuard: booleanTypeGuard
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description: "Personalizing WAC Software Page Experiment",
            expectedReleaseDate: "10/30/2025",
            expectedRuntime: "6 weeks",
            context: {
                urls: ["/software"]
            }
        }
    },
    jsmGrowth220JsmJpdWacSignup: {
        experimentName: "jsmgrowth-220_jsm_jpd_wac_signup",
        parameterName: "cohort",
        defaultValue: "control" as "control" | "variation",
        options: {
            fireExperimentExposure: true,
            typeGuard: (value: unknown): value is "control" | "variation" => {
                if (typeof value !== "string") {
                    return false;
                }
                return ["control", "variation"].includes(value);
            }
        },
        targeting: {
            cohort: () => true
        },
        metadata: {
            description:
                "Show IT Service Management template option for JSM eligible customers in Template Choice View",
            expectedReleaseDate: "04/12/2025",
            expectedRuntime: "53 days",
            context: {
                urls: ["/ondemand/signup/confirmation"]
            }
        }
    }
} satisfies Manifest<unknown>;
